import { useState, useMemo, useEffect } from "react";
import {
  Button,
  Card,
  Table,
  message,
  Input,
  Checkbox,
  Pagination,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  Typography,
  Spin,
  Descriptions,
  Empty,
} from "antd";
import {
  CheckCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  HistoryOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useGetLocations } from "~/services/common";
import { useAuth } from "~/store/auth.store";
import {
  useCreateLocationAudit,
  useGetLocationAudits,
  useUpdateLocationAudit,
} from "~/services/location-audit";
import { LocationAuditStatus } from "~/types/location-audit";
import { useNavigate } from "react-router-dom";
import { useGetInventories, useGetInventoriesInfinite } from "~/services/inventory";
import { TInventory } from "~/types/inventory";
import { useBulkUpdateInventory } from "~/services/inventory/inventory.mutations";
import { InventoryStatusCode } from "~/utils/constants";
import InfiniteScroll from "react-infinite-scroll-component";

const { Title, Text } = Typography;
const { TextArea } = Input;

type LocalAuditItem = {
  id: string;
  inventoryId: string;
  inventory: TInventory;
  isConfirmed: boolean;
  wasAdded: boolean;
};

export const ScreenVisualAudits = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [isAuditActive, setIsAuditActive] = useState(false);
  const [currentAuditId, setCurrentAuditId] = useState<string | null>(null);
  const [localAuditItems, setLocalAuditItems] = useState<LocalAuditItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isCompleteModalVisible, setIsCompleteModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isBulkUpdateModalVisible, setIsBulkUpdateModalVisible] = useState(false);
  const [showHistoryOnFirstPage, setShowHistoryOnFirstPage] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [cancelAuditId, setCancelAuditId] = useState<string | null>(null);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [bulkUpdateForm] = Form.useForm();

  const { data: locations } = useGetLocations();

  const { data: auditHistory, isLoading: isLoadingHistory } = useGetLocationAudits(
    { limit: 100 }, // Fetch all audits, not filtered by location
    { enabled: !isAuditActive } // Always fetch on first page
  );

  const { data: locationInventories, isLoading: isLoadingLocationInventories, refetch: refetchLocationInventories } =
    useGetInventories(
      {
        page: 1,
        pageSize: 500, // Backend max is 500
        locationId: selectedLocation || undefined,
      },
      { enabled: isAuditActive && !!selectedLocation }
    );

  // State for Add Inventory modal
  const [selectedInventoryIds, setSelectedInventoryIds] = useState<string[]>([]);
  const [addModalSearchTerm, setAddModalSearchTerm] = useState("");

  const {
    data: availableInventoriesData,
    fetchNextPage: fetchNextInventoryPage,
    hasNextPage: hasNextInventoryPage,
    isLoading: isLoadingInventories,
  } = useGetInventoriesInfinite(
    {
      pageSize: 50, // Increased from 20 for better performance
      search: addModalSearchTerm || undefined, // Server-side search
    },
    { enabled: isAddModalVisible }
  );

  const availableInventories = useMemo(() => {
    return availableInventoriesData?.pages.flatMap((page) => page.items) ?? [];
  }, [availableInventoriesData]);

  const createAudit = useCreateLocationAudit();
  const updateAudit = useUpdateLocationAudit();
  const bulkUpdateInventory = useBulkUpdateInventory();

  const selectedLocationName = useMemo(() => {
    return locations?.items.find(loc => loc.id === selectedLocation)?.name || "";
  }, [locations, selectedLocation]);

  const handleStartAudit = () => {
    if (!selectedLocation) {
      message.error("Please select a location");
      return;
    }
    setIsAuditActive(true);
  };

  // Initialize local audit items from location inventory
  useEffect(() => {
    if (isAuditActive && locationInventories?.items) {
      // Preserve existing confirmed state and wasAdded flag
      setLocalAuditItems((prevItems) => {
        // Create a map of existing state by inventoryId
        const existingStateMap = new Map(
          prevItems.map((item) => [
            item.inventoryId,
            { isConfirmed: item.isConfirmed, wasAdded: item.wasAdded },
          ])
        );

        // Map new inventory items while preserving existing state
        const items: LocalAuditItem[] = locationInventories.items.map((inv) => {
          const existingState = existingStateMap.get(inv.id);
          return {
            id: `local-${inv.id}`,
            inventoryId: inv.id,
            inventory: inv,
            isConfirmed: existingState?.isConfirmed || false,
            wasAdded: existingState?.wasAdded || false,
          };
        });

        // Add back any items that were added during audit but not in location inventory
        const addedItems = prevItems.filter((item) => item.wasAdded);
        const addedItemsNotInInventory = addedItems.filter(
          (addedItem) => !items.find((item) => item.inventoryId === addedItem.inventoryId)
        );

        return [...items, ...addedItemsNotInInventory];
      });
    }
  }, [isAuditActive, locationInventories]);

  const handleToggleItem = (itemId: string) => {
    setLocalAuditItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, isConfirmed: !item.isConfirmed } : item
      )
    );
  };

  const handleAddInventories = () => {
    if (selectedInventoryIds.length === 0) {
      message.warning("Please select at least one inventory item");
      return;
    }

    const inventoriesToAdd = availableInventories.filter((inv) =>
      selectedInventoryIds.includes(inv.id)
    );

    if (!inventoriesToAdd.length) return;

    const newItems: LocalAuditItem[] = inventoriesToAdd.map((inv) => ({
      id: `local-added-${inv.id}`,
      inventoryId: inv.id,
      inventory: inv,
      isConfirmed: false,
      wasAdded: true,
    }));

    setLocalAuditItems((prev) => [...prev, ...newItems]);
    message.success(`Added ${selectedInventoryIds.length} item(s) to audit`);
    setIsAddModalVisible(false);
    setSelectedInventoryIds([]);
    setAddModalSearchTerm("");
  };

  const handleSaveProgress = () => {
    if (!selectedLocation || !user?.id) {
      message.error("Missing required data");
      return;
    }

    const itemsData = localAuditItems.map((item) => ({
      inventoryId: item.inventoryId,
      isConfirmed: item.isConfirmed,
      wasAdded: item.wasAdded,
      confirmedAt: item.isConfirmed ? new Date().toISOString() : null,
    }));

    const hide = message.loading("Saving progress...", 0);

    // If we have a currentAuditId, update the existing audit
    if (currentAuditId) {
      const updateData = {
        locationId: selectedLocation,
        userId: user.id,
        notes: completionNotes,
        status: LocationAuditStatus.IN_PROGRESS,
        items: itemsData,
      };

      updateAudit.mutate(
        {
          id: currentAuditId,
          dto: updateData as any,
        },
        {
          onSuccess: () => {
            hide();
            message.success("Saved! Your progress has been updated.", 3);
          },
          onError: (error) => {
            hide();
            message.error("Failed to save progress");
            console.error(error);
          },
        }
      );
    } else {
      // Create new audit
      const auditData = {
        locationId: selectedLocation,
        userId: user.id,
        notes: completionNotes,
        status: LocationAuditStatus.IN_PROGRESS,
        items: itemsData,
      };

      createAudit.mutate(auditData as any, {
        onSuccess: (savedAudit) => {
          setCurrentAuditId(savedAudit.id);
          hide();
          message.success("Saved! You can resume this audit later.", 3);
        },
        onError: (error) => {
          hide();
          message.error("Failed to save progress");
          console.error(error);
        },
      });
    }
  };

  const handleCancelAudit = () => {
    if (!cancelAuditId) return;

    const hide = message.loading("Cancelling audit...", 0);

    updateAudit.mutate(
      {
        id: cancelAuditId,
        dto: { status: LocationAuditStatus.CANCELLED },
      },
      {
        onSuccess: () => {
          hide();
          setIsCancelModalVisible(false);
          setCancelAuditId(null);
          message.success("Audit cancelled successfully", 3);
        },
        onError: (error) => {
          hide();
          message.error("Failed to cancel audit");
          console.error(error);
        },
      }
    );
  };

  const handleOpenCancelConfirmation = (auditId: string) => {
    setCancelAuditId(auditId);
    setIsCancelModalVisible(true);
  };

  const handleCompleteAudit = () => {
    if (!selectedLocation || !user?.id) {
      message.error("Missing required data");
      return;
    }

    const hide = message.loading("Completing audit...", 0);

    // Create the audit with all data at once
    const auditData = {
      locationId: selectedLocation,
      userId: user.id,
      notes: completionNotes,
      status: LocationAuditStatus.COMPLETED,
      items: localAuditItems.map((item) => ({
        inventoryId: item.inventoryId,
        isConfirmed: item.isConfirmed,
        wasAdded: item.wasAdded,
        confirmedAt: item.isConfirmed ? new Date().toISOString() : null,
      })),
    };

    createAudit.mutate(auditData as any, {
      onSuccess: () => {
        hide();
        message.success("Audit completed and saved successfully!", 3);

        // Reset everything
        setIsCompleteModalVisible(false);
        setCompletionNotes("");
        setIsAuditActive(false);
        setLocalAuditItems([]);
        setSelectedItems([]);
        setSelectedLocation(null);
        setCurrentAuditId(null);
      },
      onError: (error) => {
        hide();
        message.error("Failed to save audit");
        console.error(error);
      },
    });
  };

  const handleResumeAudit = (audit: any) => {
    setSelectedLocation(audit.locationId);
    setCurrentAuditId(audit.id);
    setCompletionNotes(audit.notes || "");

    // Convert saved audit items to local format
    const resumedItems: LocalAuditItem[] = audit.items.map((item: any) => ({
      id: `local-${item.inventoryId}`,
      inventoryId: item.inventoryId,
      inventory: item.inventory,
      isConfirmed: item.isConfirmed,
      wasAdded: item.wasAdded,
    }));

    setLocalAuditItems(resumedItems);
    setIsAuditActive(true);
    message.info(`Resuming audit for ${audit.location?.name}`);
  };

  const handleBackToSelection = () => {
    setIsAuditActive(false);
    setLocalAuditItems([]);
    setSelectedItems([]);
    setCurrentAuditId(null);
    setCompletionNotes("");
  };

  const handleBackToInventoryControl = () => {
    navigate("/dispatch/inventory-control");
  };

  const handleBulkUpdate = (values: any) => {
    if (selectedItems.length === 0) {
      message.error("Please select items to update");
      return;
    }

    const updateData: any = {
      inventoryIds: selectedItems,
    };

    if (values.status) updateData.status = values.status;
    // Check if locationId field exists in form values (user interacted with it)
    if ('locationId' in values) {
      // Empty string or undefined means clear the location (set to null)
      updateData.locationId = values.locationId === "" || values.locationId === undefined ? null : values.locationId;
    }

    const hide = message.loading("Updating inventory...", 0);

    bulkUpdateInventory.mutate(updateData, {
      onSuccess: async () => {
        hide();
        message.success(`Successfully updated ${selectedItems.length} item(s)`, 3);
        setIsBulkUpdateModalVisible(false);
        bulkUpdateForm.resetFields();
        setSelectedItems([]);

        // Refresh the inventory list - this will preserve confirmed state via useEffect
        await refetchLocationInventories();
      },
      onError: (error) => {
        hide();
        message.error("Failed to update items");
        console.error(error);
      },
    });
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
    }
  };

  const handleSelectAllUnconfirmed = () => {
    const unconfirmedIds = localAuditItems
      .filter((item) => !item.isConfirmed)
      .map((item) => item.inventoryId);
    setSelectedItems(unconfirmedIds);
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  // Filtering and pagination
  const filteredItems = localAuditItems.filter(
    (item) =>
      item.inventory?.inventoryRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.inventory?.clientInternalRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.inventory?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.inventory?.artistName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.inventory?.packingType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageSize = 20;
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const confirmedCount = localAuditItems.filter((item) => item.isConfirmed).length;
  const totalCount = localAuditItems.length;
  const unconfirmedCount = totalCount - confirmedCount;

  const columns = [
    {
      title: "UPDATE",
      key: "select",
      width: 80,
      align: "center" as const,
      render: (_: any, record: LocalAuditItem) => (
        <Checkbox
          checked={selectedItems.includes(record.inventoryId)}
          onChange={(e) => handleSelectItem(record.inventoryId, e.target.checked)}
        />
      ),
    },
    {
      title: "CONFIRMED VISUALLY",
      key: "checkbox",
      width: 150,
      align: "center" as const,
      render: (_: any, record: LocalAuditItem) => (
        <Checkbox
          checked={record.isConfirmed}
          onChange={() => handleToggleItem(record.id)}
        />
      ),
    },
    {
      title: "Item Ref",
      dataIndex: ["inventory", "inventoryRef"],
      key: "inventoryRef",
      width: 150,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Client Ref",
      dataIndex: ["inventory", "clientInternalRef"],
      key: "clientInternalRef",
      width: 130,
    },
    {
      title: "Title",
      dataIndex: ["inventory", "title"],
      key: "title",
    },
    {
      title: "Artist",
      dataIndex: ["inventory", "artistName"],
      key: "artistName",
    },
    {
      title: "Packing Type",
      dataIndex: ["inventory", "packingType"],
      key: "packingType",
      width: 120,
    },
    {
      title: "Client",
      key: "client",
      sorter: (a: LocalAuditItem, b: LocalAuditItem) => {
        const clientA = a.inventory?.client?.companyName || `${a.inventory?.client?.firstName || ""} ${a.inventory?.client?.lastName || ""}`.trim() || "";
        const clientB = b.inventory?.client?.companyName || `${b.inventory?.client?.firstName || ""} ${b.inventory?.client?.lastName || ""}`.trim() || "";
        return clientA.localeCompare(clientB);
      },
      render: (_: any, record: LocalAuditItem) => {
        const client = record.inventory?.client;
        return client?.companyName || `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "N/A";
      },
    },
    {
      title: "Inventory Status",
      key: "inventoryStatus",
      sorter: (a: LocalAuditItem, b: LocalAuditItem) => {
        const statusA = a.inventory?.status || "";
        const statusB = b.inventory?.status || "";
        return statusA.localeCompare(statusB);
      },
      render: (_: any, record: LocalAuditItem) => (
        <Tag>{record.inventory?.status || "N/A"}</Tag>
      ),
    },
    {
      title: "Audit Status",
      key: "auditStatus",
      render: (_: any, record: LocalAuditItem) => (
        <Space>
          {record.isConfirmed && (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Confirmed
            </Tag>
          )}
          {record.wasAdded && (
            <Tag color="blue">
              <PlusOutlined /> Added During Audit
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  // Location selection view
  if (!isAuditActive) {
    return (
      <div className="p-2">
        <Card>
          <div className="flex items-center gap-4 mb-6">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToInventoryControl}
            >
              Back to Inventory Control
            </Button>
          </div>

          <Title level={3}>Visual Audit - Select Location</Title>
          <Text type="secondary">
            Select a warehouse location to begin the visual audit process
          </Text>

          <Form layout="vertical" className="mt-6">
            <Form.Item
              label="Location"
              required
              help="Choose the location you want to audit"
            >
              <Select
                size="large"
                placeholder="Select a location"
                value={selectedLocation}
                onChange={setSelectedLocation}
                showSearch
                optionFilterProp="children"
              >
                {locations?.items.map((location) => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                onClick={handleStartAudit}
                disabled={!selectedLocation}
              >
                Start Audit
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Audit History Section */}
        <Card className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Title level={4} className="mb-1">Audit History</Title>
              <Text type="secondary">View all past audits across all locations</Text>
            </div>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setShowHistoryOnFirstPage(!showHistoryOnFirstPage)}
            >
              {showHistoryOnFirstPage ? "Hide History" : "Show History"}
            </Button>
          </div>

          {showHistoryOnFirstPage && (
            <Spin spinning={isLoadingHistory}>
              {auditHistory && auditHistory.items.length > 0 ? (
                <div className="space-y-4 mt-4">
                  {auditHistory.items.map((historyAudit) => (
                    <Card key={historyAudit.id} size="small" className="bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <Descriptions size="small" column={2} className="flex-1">
                          <Descriptions.Item label="Location">
                            <Text strong>{historyAudit.location?.name || "N/A"}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Status">
                            <Tag
                              color={
                                historyAudit.status === LocationAuditStatus.COMPLETED
                                  ? "success"
                                  : "processing"
                              }
                            >
                              {historyAudit.status}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Completed By">
                            {historyAudit.user?.firstName} {historyAudit.user?.lastName}
                          </Descriptions.Item>
                          <Descriptions.Item label="Started">
                            {new Date(historyAudit.startedAt).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Completed">
                            {historyAudit.completedAt
                              ? new Date(historyAudit.completedAt).toLocaleString()
                              : "In Progress"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Items Confirmed">
                            {historyAudit.items?.filter((i) => i.isConfirmed).length || 0} /{" "}
                            {historyAudit.items?.length || 0} confirmed
                          </Descriptions.Item>
                          {historyAudit.notes && (
                            <Descriptions.Item label="Notes" span={2}>
                              {historyAudit.notes}
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                        {historyAudit.status === LocationAuditStatus.IN_PROGRESS && (
                          <Space direction="vertical" size="small">
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => handleResumeAudit(historyAudit)}
                              block
                            >
                              Resume
                            </Button>
                            <Button
                              danger
                              size="small"
                              onClick={() => handleOpenCancelConfirmation(historyAudit.id)}
                              block
                            >
                              Cancel
                            </Button>
                          </Space>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <Empty description="No audit history found" className="mt-4" />
              )}
            </Spin>
          )}
        </Card>
      </div>
    );
  }

  // Audit in progress view
  return (
    <div className="p-2">
      <Spin spinning={isLoadingLocationInventories}>
        <Card>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <Button icon={<ArrowLeftOutlined />} onClick={handleBackToSelection}>
                Back
              </Button>
              <div>
                <Title level={3} className="mb-0">
                  Visual Audit - {selectedLocationName}
                </Title>
                <Text type="secondary">In Progress</Text>
              </div>
            </div>
            <Space>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setIsAddModalVisible(true)}
              >
                Add Inventory
              </Button>
              {selectedItems.length > 0 && (
                <Button
                  icon={<EditOutlined />}
                  type="default"
                  onClick={() => setIsBulkUpdateModalVisible(true)}
                >
                  Update {selectedItems.length} Selected
                </Button>
              )}
              <Button
                type="default"
                onClick={handleSaveProgress}
                loading={createAudit.isPending || updateAudit.isPending}
              >
                Save Progress
              </Button>
              <Button type="primary" onClick={() => setIsCompleteModalVisible(true)}>
                Complete Audit
              </Button>
            </Space>
          </div>

          <div className="mb-4">
            <Space direction="vertical" style={{ width: "100%" }}>
              <div className="flex justify-between items-center">
                <div>
                  <Tag color="processing">IN PROGRESS</Tag>
                  <Text className="ml-4">
                    Progress: {confirmedCount} / {totalCount} items confirmed (
                    {totalCount > 0 ? Math.round((confirmedCount / totalCount) * 100) : 0}
                    %)
                  </Text>
                  {unconfirmedCount > 0 && (
                    <Text className="ml-4" type="warning">
                      {unconfirmedCount} unconfirmed
                    </Text>
                  )}
                </div>
                <Input
                  placeholder="Search by item ref, client ref, title, artist, or packing type..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 400 }}
                />
              </div>
              {unconfirmedCount > 0 && (
                <Space>
                  <Button size="small" onClick={handleSelectAllUnconfirmed}>
                    Select All Unconfirmed ({unconfirmedCount})
                  </Button>
                  {selectedItems.length > 0 && (
                    <Button size="small" onClick={handleDeselectAll}>
                      Deselect All
                    </Button>
                  )}
                </Space>
              )}
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={paginatedItems}
            rowKey="id"
            pagination={false}
            size="small"
          />

          <div className="mt-4 flex justify-end">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredItems.length}
              onChange={setCurrentPage}
              showSizeChanger={false}
              showTotal={(total) => `Total ${total} items`}
            />
          </div>
        </Card>
      </Spin>

      {/* Add Inventory Modal */}
      <Modal
        title={
          <div className="flex items-center justify-between border-b border-gray-200 pb-3 -mx-6 px-6">
            <span className="text-lg font-semibold text-gray-900">Add Inventory to Audit</span>
            <div className="text-sm font-normal text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              Selected: <span className="font-semibold text-blue-600">{selectedInventoryIds.length}</span>
              {addModalSearchTerm && (
                <span className="ml-2">
                  (Search results: <span className="font-semibold text-green-600">{availableInventories.length}</span>)
                </span>
              )}
            </div>
          </div>
        }
        open={isAddModalVisible}
        onCancel={() => {
          setIsAddModalVisible(false);
          setSelectedInventoryIds([]);
          setAddModalSearchTerm("");
        }}
        width="90%"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => {
              setIsAddModalVisible(false);
              setSelectedInventoryIds([]);
              setAddModalSearchTerm("");
            }}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleAddInventories}>
              Add Selected Items ({selectedInventoryIds.length})
            </Button>
          </div>
        }
      >
        {/* Search Input */}
        <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <Input
            placeholder="Search by item ref, client ref, title, artist, or packing type..."
            prefix={<SearchOutlined />}
            value={addModalSearchTerm}
            onChange={(e) => setAddModalSearchTerm(e.target.value)}
            allowClear
            className="w-full shadow-sm"
            size="large"
          />
        </div>

        <InfiniteScroll
          dataLength={availableInventories.length}
          next={fetchNextInventoryPage}
          hasMore={!!hasNextInventoryPage}
          loader={<div className="text-center py-4">Loading more items...</div>}
          height={500}
          endMessage={
            <div className="text-center py-4 text-gray-500">
              {availableInventories.length === 0
                ? addModalSearchTerm
                  ? "No inventory items found matching your search"
                  : "No inventory items found"
                : "All items loaded"}
            </div>
          }
        >
          <Table
            rowKey="id"
            columns={[
              {
                title: "Item Ref",
                dataIndex: "inventoryRef",
                key: "inventoryRef",
                width: "12%",
                render: (text: string) => <Text strong>{text}</Text>,
              },
              {
                title: "Client Ref",
                dataIndex: "clientInternalRef",
                key: "clientInternalRef",
                width: "12%",
                render: (value) => value || "N/A",
              },
              {
                title: "Title",
                dataIndex: "title",
                key: "title",
                width: "20%",
              },
              {
                title: "Artist",
                dataIndex: "artistName",
                key: "artistName",
                width: "15%",
                render: (value) => value || "N/A",
              },
              {
                title: "Packing Type",
                dataIndex: "packingType",
                key: "packingType",
                width: "12%",
                render: (value) => value || "N/A",
              },
              {
                title: "Location",
                dataIndex: ["location", "name"],
                key: "location",
                width: "12%",
                render: (value) => value || "N/A",
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: "10%",
                render: (status) => <Tag>{status}</Tag>,
              },
              {
                title: "Action",
                key: "action",
                align: "center",
                width: "10%",
                render: (_: any, record: TInventory) => {
                  const alreadyInAudit = localAuditItems.some(
                    (item) => item.inventoryId === record.id
                  );
                  const isSelected = selectedInventoryIds.includes(record.id);

                  if (alreadyInAudit) {
                    return <Text type="secondary">In Audit</Text>;
                  }

                  return (
                    <Button
                      size="small"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedInventoryIds(prev => prev.filter(id => id !== record.id));
                        } else {
                          setSelectedInventoryIds(prev => [...prev, record.id]);
                        }
                      }}
                      type={isSelected ? "default" : "primary"}
                    >
                      {isSelected ? "Remove" : "Add"}
                    </Button>
                  );
                },
              },
            ]}
            dataSource={availableInventories}
            pagination={false}
            loading={isLoadingInventories && availableInventories.length === 0}
            rowClassName={(record) =>
              selectedInventoryIds.includes(record.id)
                ? "bg-blue-50 border-l-4 border-l-blue-500"
                : localAuditItems.some((item) => item.inventoryId === record.id)
                ? "bg-gray-100"
                : ""
            }
            size="small"
          />
        </InfiniteScroll>
      </Modal>

      {/* Complete Audit Modal */}
      <Modal
        title="Complete Audit"
        open={isCompleteModalVisible}
        onOk={handleCompleteAudit}
        onCancel={() => setIsCompleteModalVisible(false)}
        okText="Complete Audit"
        confirmLoading={createAudit.isPending}
      >
        <div className="mb-4">
          <Text>
            You have confirmed {confirmedCount} out of {totalCount} items.
          </Text>
          {confirmedCount < totalCount && (
            <Text type="warning" className="block mt-2">
              Warning: Not all items have been confirmed.
            </Text>
          )}
        </div>
        <Form.Item label="Completion Notes (Optional)">
          <TextArea
            rows={4}
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder="Add any notes about this audit..."
          />
        </Form.Item>
      </Modal>

      {/* History Modal */}
      <Modal
        title="All Audit History"
        open={isHistoryModalVisible}
        onCancel={() => setIsHistoryModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setIsHistoryModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Spin spinning={isLoadingHistory}>
          {auditHistory && auditHistory.items.length > 0 ? (
            <div className="space-y-4">
              {auditHistory.items.map((historyAudit) => (
                <Card key={historyAudit.id} size="small">
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Location">
                      <Text strong>{historyAudit.location?.name || "N/A"}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag
                        color={
                          historyAudit.status === LocationAuditStatus.COMPLETED
                            ? "success"
                            : "processing"
                        }
                      >
                        {historyAudit.status}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Completed By">
                      {historyAudit.user?.firstName} {historyAudit.user?.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Started">
                      {new Date(historyAudit.startedAt).toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Completed">
                      {historyAudit.completedAt
                        ? new Date(historyAudit.completedAt).toLocaleString()
                        : "In Progress"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Items Confirmed">
                      {historyAudit.items?.filter((i) => i.isConfirmed).length || 0} /{" "}
                      {historyAudit.items?.length || 0} confirmed
                    </Descriptions.Item>
                    {historyAudit.notes && (
                      <Descriptions.Item label="Notes" span={2}>
                        {historyAudit.notes}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              ))}
            </div>
          ) : (
            <Empty description="No audit history found" />
          )}
        </Spin>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal
        title={`Bulk Update ${selectedItems.length} Selected Items`}
        open={isBulkUpdateModalVisible}
        onCancel={() => {
          setIsBulkUpdateModalVisible(false);
          bulkUpdateForm.resetFields();
        }}
        onOk={() => bulkUpdateForm.submit()}
        confirmLoading={bulkUpdateInventory.isPending}
      >
        <Form form={bulkUpdateForm} layout="vertical" onFinish={handleBulkUpdate}>
          <Form.Item label="Status" name="status">
            <Select placeholder="Update status (optional)" allowClear>
              {Object.keys(InventoryStatusCode).map((status) => (
                <Select.Option key={status} value={status}>
                  {status.replace(/_/g, " ")}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Location" name="locationId">
            <Select
              placeholder="Update location (optional)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              <Select.Option value="">None (Clear Location)</Select.Option>
              {locations?.items.map((location) => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Text type="secondary">
            Note: Only selected fields will be updated. Leave empty to keep current
            values.
          </Text>
        </Form>
      </Modal>

      {/* Cancel Audit Confirmation Modal */}
      <Modal
        title="Cancel Audit"
        open={isCancelModalVisible}
        onOk={handleCancelAudit}
        onCancel={() => {
          setIsCancelModalVisible(false);
          setCancelAuditId(null);
        }}
        okText="Yes, Cancel Audit"
        okButtonProps={{ danger: true }}
        confirmLoading={updateAudit.isPending}
      >
        <Text>
          Are you sure you want to cancel this audit? This action cannot be undone.
          The audit will be marked as cancelled and cannot be resumed.
        </Text>
      </Modal>
    </div>
  );
};
